import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Gift-voucher panel on the vouchers page. The amount chips choose the gift-card variant and
 * "Gutschein erstellen" reveals the recipient form. Adding to the cart is handled by the nested
 * <product-form-component>. The chips sit outside it on purpose: refs belong to the closest
 * Component, so chips inside the form would be the product form's refs, not ours.
 */
class VouchersGiftComponent extends Component {
  // revealButton is optional: without a label the section renders the details open.
  requiredRefs = ['details'];

  /** @type {number | undefined} */
  #releaseTimer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.querySelector('.vouchers-gift__form')?.addEventListener('submit', this.#onSubmit);
    this.addEventListener(ThemeEvents.cartError, this.#release);
    window.addEventListener('pageshow', this.#syncVariant);
    this.#syncVariant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.querySelector('.vouchers-gift__form')?.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener(ThemeEvents.cartError, this.#release);
    window.removeEventListener('pageshow', this.#syncVariant);
    clearTimeout(this.#releaseTimer);
  }

  selectAmount = () => {
    this.#syncVariant();
  };

  /**
   * Copies the checked chip into the form's variant id. Runs on connect and on pageshow too:
   * the chips sit outside the <form>, so the browser restores their checked state on reload or
   * back/forward navigation while the hidden id input keeps its Liquid default.
   */
  #syncVariant = () => {
    const checked = this.querySelector('.vouchers-gift__amount-input:checked');
    const variantInput = this.querySelector('product-form-component input[name="id"]');

    if (checked instanceof HTMLInputElement && variantInput instanceof HTMLInputElement) {
      variantInput.value = checked.value;
    }
  };

  reveal = () => {
    const { revealButton, details } = this.refs;

    if (!(details instanceof HTMLElement) || !(revealButton instanceof HTMLElement)) return;

    details.hidden = false;
    revealButton.setAttribute('aria-expanded', 'true');
    revealButton.hidden = true;

    const firstField = details.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled])');
    if (firstField instanceof HTMLElement) firstField.focus();
  };

  #onSubmit = () => {
    const button = this.querySelector('.vouchers-gift__form [name="add"]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = true;

    // product-form.js doesn't disable the button while /cart/add.js is in flight, so a
    // double-click would add two gift cards; the timeout releases it even if the request
    // never resolves (cartUpdate/cartError not dispatched).
    clearTimeout(this.#releaseTimer);
    this.#releaseTimer = window.setTimeout(this.#release, 10000);
  };

  #release = () => {
    clearTimeout(this.#releaseTimer);

    const button = this.querySelector('.vouchers-gift__form [name="add"]');
    if (button instanceof HTMLButtonElement) button.disabled = false;
  };

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    this.#release();

    const { detail } = /** @type {Event & { detail?: { sourceId?: string, data?: { source?: string, didError?: boolean } } }} */ (event);

    if (detail?.data?.source !== 'product-form-component' || detail.data.didError) return;

    const checked = this.querySelector('.vouchers-gift__amount-input:checked');
    const props = {
      variant_id: detail.sourceId ?? null,
      amount: checked instanceof HTMLInputElement ? checked.dataset.amount ?? null : null,
    };

    window.phCapture?.('vouchers_gift_card_added', props);
  };
}

if (!customElements.get('vouchers-gift-component')) {
  customElements.define('vouchers-gift-component', VouchersGiftComponent);
}
